import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UserGroupIcon,
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  BellIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { CalendarDaysIcon as CalendarDaysIconSolid } from '@heroicons/react/24/solid';

const PersonalCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // month, week, day, agenda
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEventType, setSelectedEventType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    // Simulate fetching personal calendar events
    const fetchEvents = () => {
      const mockEvents = [
        {
          id: 'event001',
          title: 'Team Meeting',
          description: 'Weekly team sync-up to discuss project progress and upcoming tasks',
          type: 'meeting',
          status: 'confirmed',
          start: new Date(2024, 11, 15, 10, 0),
          end: new Date(2024, 11, 15, 11, 0),
          location: 'Conference Room A',
          attendees: ['Dr. Sarah Johnson', 'Mike Wilson', 'Lisa Chen'],
          organizer: 'Current User',
          priority: 'high',
          reminder: '15min',
          recurring: 'weekly',
          color: 'blue',
          notes: 'Please bring your project status reports'
        },
        {
          id: 'event002',
          title: 'Patient Consultation',
          description: 'Consultation with patient regarding treatment plan',
          type: 'appointment',
          status: 'confirmed',
          start: new Date(2024, 11, 15, 14, 30),
          end: new Date(2024, 11, 15, 15, 30),
          location: 'Room 205',
          attendees: ['John Smith (Patient)', 'Nurse Mary'],
          organizer: 'Current User',
          priority: 'high',
          reminder: '30min',
          recurring: 'none',
          color: 'green',
          notes: 'Follow-up on previous treatment'
        },
        {
          id: 'event003',
          title: 'Medical Conference',
          description: 'Annual healthcare innovation conference',
          type: 'conference',
          status: 'tentative',
          start: new Date(2024, 11, 16, 9, 0),
          end: new Date(2024, 11, 16, 17, 0),
          location: 'Grand Auditorium',
          attendees: ['Multiple speakers', '200+ attendees'],
          organizer: 'Conference Committee',
          priority: 'medium',
          reminder: '1hour',
          recurring: 'yearly',
          color: 'purple',
          notes: 'Registration required'
        },
        {
          id: 'event004',
          title: 'Department Review',
          description: 'Monthly department performance review',
          type: 'review',
          status: 'confirmed',
          start: new Date(2024, 11, 17, 13, 0),
          end: new Date(2024, 11, 17, 14, 30),
          location: 'Executive Boardroom',
          attendees: ['Department Head', 'Senior Staff'],
          organizer: 'HR Department',
          priority: 'high',
          reminder: '45min',
          recurring: 'monthly',
          color: 'red',
          notes: 'Prepare monthly reports'
        },
        {
          id: 'event005',
          title: 'Training Session',
          description: 'New medical equipment training',
          type: 'training',
          status: 'confirmed',
          start: new Date(2024, 11, 18, 11, 0),
          end: new Date(2024, 11, 18, 12, 30),
          location: 'Training Center',
          attendees: ['Medical Staff', 'Technical Support'],
          organizer: 'Training Department',
          priority: 'medium',
          reminder: '20min',
          recurring: 'none',
          color: 'orange',
          notes: 'Mandatory attendance'
        },
        {
          id: 'event006',
          title: 'Research Meeting',
          description: 'Discuss ongoing research project',
          type: 'research',
          status: 'pending',
          start: new Date(2024, 11, 19, 15, 0),
          end: new Date(2024, 11, 19, 16, 0),
          location: 'Research Lab',
          attendees: ['Research Team', 'Principal Investigator'],
          organizer: 'Research Department',
          priority: 'medium',
          reminder: '30min',
          recurring: 'bi-weekly',
          color: 'indigo',
          notes: 'Review latest findings'
        },
        {
          id: 'event007',
          title: 'Emergency Drill',
          description: 'Hospital-wide emergency response drill',
          type: 'drill',
          status: 'confirmed',
          start: new Date(2024, 11, 20, 16, 0),
          end: new Date(2024, 11, 20, 16, 30),
          location: 'Hospital Wide',
          attendees: ['All Staff'],
          organizer: 'Safety Committee',
          priority: 'high',
          reminder: '1hour',
          recurring: 'quarterly',
          color: 'yellow',
          notes: 'Participation mandatory'
        },
        {
          id: 'event008',
          title: 'Lunch Break',
          description: 'Personal lunch break',
          type: 'personal',
          status: 'confirmed',
          start: new Date(2024, 11, 15, 12, 0),
          end: new Date(2024, 11, 15, 13, 0),
          location: 'Cafeteria',
          attendees: [],
          organizer: 'Current User',
          priority: 'low',
          reminder: 'none',
          recurring: 'daily',
          color: 'gray',
          notes: 'Personal time'
        }
      ];

      setEvents(mockEvents);
      setLoading(false);
    };

    setTimeout(fetchEvents, 500);
  }, []);

  const eventTypes = [
    { id: 'all', name: 'All Events', color: 'gray' },
    { id: 'meeting', name: 'Meetings', color: 'blue' },
    { id: 'appointment', name: 'Appointments', color: 'green' },
    { id: 'conference', name: 'Conferences', color: 'purple' },
    { id: 'training', name: 'Training', color: 'orange' },
    { id: 'research', name: 'Research', color: 'indigo' },
    { id: 'personal', name: 'Personal', color: 'gray' },
    { id: 'review', name: 'Reviews', color: 'red' },
    { id: 'drill', name: 'Drills', color: 'yellow' }
  ];

  const statusTypes = [
    { id: 'all', name: 'All Status' },
    { id: 'confirmed', name: 'Confirmed' },
    { id: 'tentative', name: 'Tentative' },
    { id: 'pending', name: 'Pending' },
    { id: 'cancelled', name: 'Cancelled' }
  ];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    // eslint-disable-next-line no-unused-vars
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    for (let i = 0; i < 42; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }

    return days;
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.location.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = selectedEventType === 'all' || event.type === selectedEventType;
    const matchesStatus = selectedStatus === 'all' || event.status === selectedStatus;

    return matchesSearch && matchesType && matchesStatus;
  });

  const getEventColor = (event) => {
    const colors = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      red: 'bg-red-500',
      orange: 'bg-orange-500',
      indigo: 'bg-indigo-500',
      yellow: 'bg-yellow-500',
      gray: 'bg-gray-500'
    };
    return colors[event.color] || 'bg-blue-500';
  };

  const getEventBgColor = (event) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200',
      green: 'bg-green-50 border-green-200',
      purple: 'bg-purple-50 border-purple-200',
      red: 'bg-red-50 border-red-200',
      orange: 'bg-orange-50 border-orange-200',
      indigo: 'bg-indigo-50 border-indigo-200',
      yellow: 'bg-yellow-50 border-yellow-200',
      gray: 'bg-gray-50 border-gray-200'
    };
    return colors[event.color] || 'bg-blue-50 border-blue-200';
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
      case 'medium':
        return <div className="w-4 h-4 rounded-full bg-yellow-400"></div>;
      case 'low':
        return <div className="w-4 h-4 rounded-full bg-green-400"></div>;
      default:
        return null;
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const navigateDay = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + direction);
    setCurrentDate(newDate);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSameMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  const openEventModal = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center">
                <CalendarDaysIconSolid className="h-8 w-8 text-blue-600 mr-3" />
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Personal Calendar</h1>
                  <p className="mt-2 text-sm text-gray-700">
                    Manage your personal schedule and appointments
                  </p>
                </div>
              </div>
              <div className="mt-4 sm:mt-0 flex items-center space-x-3">
                <Link
                  to="/calendar/schedule"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Event
                </Link>
                <Link
                  to="/calendar"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  All Calendars
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Controls */}
        <div className="bg-white shadow rounded-lg p-4 lg:p-6 mb-6">
          <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
            {/* Navigation and View Controls */}
            <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => view === 'month' ? navigateMonth(-1) : view === 'week' ? navigateWeek(-1) : navigateDay(-1)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                >
                  <ChevronLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <div className="text-base sm:text-lg font-semibold text-gray-900 min-w-[180px] sm:min-w-[200px] text-center">
                  {view === 'month' && currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  {view === 'week' && `Week of ${currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                  {view === 'day' && currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  {view === 'agenda' && 'Agenda View'}
                </div>
                <button
                  onClick={() => view === 'month' ? navigateMonth(1) : view === 'week' ? navigateWeek(1) : navigateDay(1)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                >
                  <ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>

              <button
                onClick={() => {
                  setCurrentDate(new Date());
                }}
                className="px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
              >
                Today
              </button>
            </div>

            {/* View Toggle */}
            <div className="flex items-center justify-center sm:justify-start">
              <div className="inline-flex rounded-md shadow-sm">
                {['month', 'week', 'day', 'agenda'].map((viewType) => (
                  <button
                    key={viewType}
                    onClick={() => setView(viewType)}
                    className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium ${
                      view === viewType
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    } ${
                      viewType === 'month' ? 'rounded-l-md' :
                      viewType === 'agenda' ? 'rounded-r-md -ml-px' :
                      '-ml-px'
                    } border border-gray-300`}
                  >
                    <span className="hidden sm:inline">{viewType.charAt(0).toUpperCase() + viewType.slice(1)}</span>
                    <span className="sm:hidden">{viewType.charAt(0).toUpperCase()}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-3">
              <div className="relative w-full sm:w-auto">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full sm:w-64 pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Filters</span>
              </button>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Event Type
                  </label>
                  <select
                    value={selectedEventType}
                    onChange={(e) => setSelectedEventType(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {eventTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {statusTypes.map(status => (
                      <option key={status.id} value={status.id}>{status.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4 flex flex-col sm:flex-row justify-end">
                <button
                  onClick={() => {
                    setSelectedEventType('all');
                    setSelectedStatus('all');
                    setSearchTerm('');
                  }}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Calendar Views */}
        {view === 'month' && (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            {/* Month View Header */}
            <div className="grid grid-cols-7 gap-px bg-gray-200">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                <div key={day} className="bg-gray-50 py-2 px-1">
                  <div className="font-semibold text-xs sm:text-sm text-gray-900 text-center">
                    <span className="hidden sm:inline">{day}</span>
                    <span className="sm:hidden">{day.charAt(0)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Month View Body */}
            <div className="grid grid-cols-7 gap-px bg-gray-200">
              {getDaysInMonth(currentDate).map((day, index) => (
                <div
                  key={index}
                  className={`bg-white min-h-[120px] p-1 ${
                    !isSameMonth(day) ? 'bg-gray-50' : ''
                  }`}
                >
                  <div className={`text-sm font-medium mb-1 ${
                    isToday(day) 
                      ? 'bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center' 
                      : isSameMonth(day) 
                        ? 'text-gray-900' 
                        : 'text-gray-400'
                  }`}>
                    {day.getDate()}
                  </div>
                  <div className="space-y-1">
                    {getEventsForDate(day).slice(0, 3).map(event => (
                      <div
                        key={event.id}
                        onClick={() => openEventModal(event)}
                        className={`text-xs p-1 rounded cursor-pointer hover:opacity-80 ${getEventBgColor(event)}`}
                      >
                        <div className="flex items-center">
                          <div className={`w-2 h-2 rounded-full mr-1 ${getEventColor(event)}`}></div>
                          <span className="truncate">{event.title}</span>
                        </div>
                      </div>
                    ))}
                    {getEventsForDate(day).length > 3 && (
                      <div className="text-xs text-gray-500 text-center">
                        +{getEventsForDate(day).length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {view === 'agenda' && (
          <div className="space-y-6">
            {filteredEvents.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No events found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Try adjusting your search or filter criteria.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredEvents
                  .sort((a, b) => new Date(a.start) - new Date(b.start))
                  .map(event => (
                    <div key={event.id} className={`bg-white rounded-lg shadow p-4 lg:p-6 border-l-4 ${getEventColor(event)}`}>
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:items-center mb-2 space-y-2 sm:space-y-0">
                            <h3 className="text-lg font-semibold text-gray-900 sm:mr-3">
                              {event.title}
                            </h3>
                            <div className="flex items-center space-x-2">
                              {getPriorityIcon(event.priority)}
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                event.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                event.status === 'tentative' ? 'bg-yellow-100 text-yellow-800' :
                                event.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {event.status}
                              </span>
                            </div>
                          </div>

                          <p className="text-gray-600 mb-4 text-sm sm:text-base">{event.description}</p>

                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 text-sm text-gray-600">
                            <div className="flex items-center">
                              <ClockIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                              <span className="truncate">{formatTime(event.start)} - {formatTime(event.end)}</span>
                            </div>
                            <div className="flex items-center">
                              <CalendarIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                              <span className="truncate">{event.start.toLocaleDateString()}</span>
                            </div>
                            {event.location && (
                              <div className="flex items-center">
                                <MapPinIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                                <span className="truncate">{event.location}</span>
                              </div>
                            )}
                          </div>

                          {event.attendees.length > 0 && (
                            <div className="mt-3 flex items-start text-sm text-gray-600">
                              <UserGroupIcon className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                              <span className="break-words">
                                {event.attendees.slice(0, 2).join(', ')}
                                {event.attendees.length > 2 && ` +${event.attendees.length - 2} more`}
                              </span>
                            </div>
                          )}

                          {event.notes && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-md">
                              <p className="text-sm text-gray-700">{event.notes}</p>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 mt-4 sm:mt-0 sm:ml-4">
                          <button
                            onClick={() => openEventModal(event)}
                            className="w-full sm:w-auto inline-flex items-center justify-center sm:justify-start px-3 py-2 sm:p-2 text-sm font-medium text-gray-600 bg-gray-100 sm:bg-transparent hover:text-gray-800 rounded-md hover:bg-gray-200 sm:hover:bg-gray-100"
                            title="View details"
                          >
                            <EyeIcon className="h-4 w-4 sm:mr-0 mr-2" />
                            <span className="sm:hidden">View Details</span>
                          </button>
                          <button
                            className="w-full sm:w-auto inline-flex items-center justify-center sm:justify-start px-3 py-2 sm:p-2 text-sm font-medium text-gray-600 bg-gray-100 sm:bg-transparent hover:text-gray-800 rounded-md hover:bg-gray-200 sm:hover:bg-gray-100"
                            title="Edit event"
                          >
                            <PencilIcon className="h-4 w-4 sm:mr-0 mr-2" />
                            <span className="sm:hidden">Edit Event</span>
                          </button>
                          <button
                            className="w-full sm:w-auto inline-flex items-center justify-center sm:justify-start px-3 py-2 sm:p-2 text-sm font-medium text-red-600 bg-red-50 sm:bg-transparent hover:text-red-800 rounded-md hover:bg-red-100 sm:hover:bg-red-50"
                            title="Delete event"
                          >
                            <TrashIcon className="h-4 w-4 sm:mr-0 mr-2" />
                            <span className="sm:hidden">Delete</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Upcoming Events Widget */}
        <div className="mt-8 bg-white shadow rounded-lg p-4 lg:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-2 sm:space-y-0">
            <h3 className="text-lg font-semibold text-gray-900">Upcoming Events</h3>
            <Link
              to="/calendar/schedule"
              className="text-sm text-blue-600 hover:text-blue-800 font-medium self-start sm:self-auto"
            >
              Schedule New
            </Link>
          </div>

          <div className="space-y-3">
            {events
              .filter(event => new Date(event.start) > new Date())
              .sort((a, b) => new Date(a.start) - new Date(b.start))
              .slice(0, 5)
              .map(event => (
                <div key={event.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gray-50 rounded-lg space-y-2 sm:space-y-0">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-3 flex-shrink-0 ${getEventColor(event)}`}></div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900 truncate">{event.title}</div>
                      <div className="text-sm text-gray-600">
                        <span className="block sm:inline">{event.start.toLocaleDateString()}</span>
                        <span className="block sm:inline sm:ml-1">at {formatTime(event.start)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 self-end sm:self-auto">
                    {event.reminder !== 'none' && (
                      <BellIcon className="h-4 w-4 text-gray-400" />
                    )}
                    {event.recurring !== 'none' && (
                      <ArrowPathIcon className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </div>
              ))}

            {events.filter(event => new Date(event.start) > new Date()).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No upcoming events
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Event Modal */}  
      {showEventModal && selectedEvent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-11/12 sm:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-2 sm:space-y-0">
              <h3 className="text-lg font-semibold text-gray-900">
                Event Details
              </h3>
              <button
                onClick={() => setShowEventModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  {selectedEvent.title}
                </h4>
                <p className="text-gray-600">{selectedEvent.description}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Date & Time</div>
                  <div className="text-sm text-gray-900">
                    {selectedEvent.start.toLocaleDateString()}
                  </div>
                  <div className="text-sm text-gray-900">
                    {formatTime(selectedEvent.start)} - {formatTime(selectedEvent.end)}
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Location</div>
                  <div className="text-sm text-gray-900">{selectedEvent.location}</div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Status</div>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    selectedEvent.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                    selectedEvent.status === 'tentative' ? 'bg-yellow-100 text-yellow-800' :
                    selectedEvent.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {selectedEvent.status}
                  </span>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Priority</div>
                  <div className="flex items-center">
                    {getPriorityIcon(selectedEvent.priority)}
                    <span className="ml-2 text-sm text-gray-900 capitalize">
                      {selectedEvent.priority}
                    </span>
                  </div>
                </div>
              </div>

              {selectedEvent.attendees.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">Attendees</div>
                  <div className="space-y-1">
                    {selectedEvent.attendees.map((attendee, index) => (
                      <div key={index} className="text-sm text-gray-900">
                        {attendee}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedEvent.notes && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">Notes</div>
                  <div className="p-3 bg-gray-50 rounded-md">
                    <p className="text-sm text-gray-700">{selectedEvent.notes}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t">
              <button
                onClick={() => setShowEventModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
              <Link
                to={`/calendar/schedule?edit=${selectedEvent.id}`}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                Edit Event
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalCalendar;
